import ky, { type Options } from "ky";
import { type z } from "zod";

import { AjaxError } from "./AjaxError";
import {
  InternalServerException,
  ResourceNotFoundException,
  VisitorNotFoundException,
} from "./exceptions";
import { type IHttpServiceClient } from "./IHttpServiceClient";

type ValidationMode = "off" | "warn" | "throw";

export interface IKyClientOptions extends Options {
  schema?: z.ZodSchema;
  validate?: ValidationMode;
}

function validateResponse(
  schema: z.ZodSchema | undefined,
  data: unknown,
  mode: ValidationMode = import.meta.env.DEV ? "warn" : "off"
): unknown {
  if (!schema || mode === "off") return data;

  const result = schema.safeParse(data);
  if (!result.success) {
    const issues = result.error.issues.map((i) => ({
      path: i.path.join("."),
      message: i.message,
      code: i.code,
    }));
    if (mode === "throw") throw result.error;
    console.warn("[Schema validation]", { issues });
    return data;
  }
  return result.data;
}

export class KyClient implements IHttpServiceClient<IKyClientOptions> {
  public options: IKyClientOptions;
  private kyInstance: ReturnType<(typeof ky)["create"]>;

  constructor(options: IKyClientOptions) {
    this.options = options;
    this.kyInstance = ky.create({
      ...options,
      hooks: {
        beforeError: [
          async (error) => {
            const { response, request, options } = error;

            if (response) {
              let apiMessage: string | undefined;
              try {
                const body = (await response.clone().json()) as {
                  error?: { message?: string };
                  message?: string;
                };
                apiMessage = body?.message ?? body?.error?.message;
                if (body?.error?.message === "Visitor not found") {
                  return new VisitorNotFoundException(
                    response,
                    request,
                    options
                  );
                }
              } catch {
                // non-JSON body — fall through to status-based handling
              }

              if (request?.method === "GET" && response.status === 404) {
                return new ResourceNotFoundException(
                  response,
                  request,
                  options
                );
              }

              if (response.status) {
                return new AjaxError(
                  response.status,
                  response,
                  request,
                  options,
                  apiMessage ?? `Ajax error occurred (${response.status})`
                );
              }
            }

            return new InternalServerException(response, request, options);
          },
        ],
      },
    });
  }

  public async get<R = unknown>(
    url: string,
    options?: IKyClientOptions
  ): Promise<R> {
    const { schema, validate, ...kyOptions } = options ?? {};
    const data = await this.kyInstance.get(url, kyOptions).json();
    return validateResponse(schema, data, validate) as R;
  }

  public async post<R = unknown, B = unknown>(
    url: string,
    body: B,
    options?: IKyClientOptions
  ): Promise<R> {
    const { schema, validate, ...kyOptions } = options ?? {};
    const data = await this.kyInstance
      .post(url, { json: body, ...kyOptions })
      .json();
    return validateResponse(schema, data, validate) as R;
  }

  public async put<R = unknown, B = unknown>(
    url: string,
    body?: B,
    options?: IKyClientOptions
  ): Promise<R> {
    const { schema, validate, ...kyOptions } = options ?? {};
    const data = await this.kyInstance
      .put(url, { json: body, ...kyOptions })
      .json();
    return validateResponse(schema, data, validate) as R;
  }

  public async patch<R = unknown, B = unknown>(
    url: string,
    body?: B,
    options?: IKyClientOptions
  ): Promise<R> {
    const { schema, validate, ...kyOptions } = options ?? {};
    const data = await this.kyInstance
      .patch(url, { json: body, ...kyOptions })
      .json();
    return validateResponse(schema, data, validate) as R;
  }

  public async delete<R = unknown, B = unknown>(
    url: string,
    body?: B,
    options?: IKyClientOptions
  ): Promise<R> {
    const { schema, validate, ...kyOptions } = options ?? {};
    const data = await this.kyInstance
      .delete(url, { json: body, ...kyOptions })
      .json();
    return validateResponse(schema, data, validate) as R;
  }
}
